import { useFormik } from "formik";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useMapMarkerStore, usePipedriveStore, useSaleCompStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useSaleCompDelete from "../../hooks/useSaleCompDelete";
import useSaleCompUpdate from "../../hooks/useSaleCompUpdate";
import useSaleCompValidationSchema from "../../hooks/useSaleCompValidationSchema";
import DetailsType from "../../interfaces/DetailsType";
import { ISaleCompState, SaleCompState } from "../../interfaces/ISaleComp";
import { ClosableOrder } from "../../store/ClosableStore";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanel from "../DetailsPanel";
import EditControls, { DeleteControl } from "../EditControls";
import SaleCompIcon from "../icons/SaleCompIcon";
import OnMapSpinner from "../spinners/OnMapSpinner";
import SaleCompFields from "./SaleCompFields";

function SaleCompEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const updateSaleComp = useSaleCompUpdate();
    const deleteSaleComp = useSaleCompDelete();
    const saleCompStore = useSaleCompStore();
    const pipedriveStore = usePipedriveStore();
    const mapMarkerStore = useMapMarkerStore();
    const saleComp = useMemo(() => computed(() => saleCompStore.saleComps.find(sc => sc.id.toString() === id)), [id]).get();
    const buyerPipedriveOrganization = useMemo(() => computed(() => saleComp && pipedriveStore.organizations.find(o => o.id === saleComp.pipedriveBuyerId)), [saleComp]).get();
    const sellerPipedriveOrganization = useMemo(() => computed(() => saleComp && pipedriveStore.organizations.find(o => o.id === saleComp.pipedriveSellerId)), [saleComp]).get();
    const brokeragePipedriveOrganization = useMemo(() => computed(() => saleComp && pipedriveStore.organizations.find(o => o.id === saleComp.pipedriveBrokerageId)), [saleComp]).get();
    const validationSchema = useSaleCompValidationSchema();

    const close = () => navigate("/");
    const cancel = () => navigate(`/saleComps/${saleComp!.id}`);
    useClosable(ClosableOrder.Medium, cancel);

    const formik = useFormik<ISaleCompState>({
        initialValues: SaleCompState.empty(),
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            await updateSaleComp(saleComp!, values);
            cancel();
        },
    });

    const remove = async () => {
        await deleteSaleComp(saleComp!.id);
        close();
    }

    useEffect(() => {
        if (saleComp) {
            saleCompStore.setEditedSaleCompId(saleComp.id);
            mapMarkerStore.draw(saleComp.centerPoint);
            formik.resetForm({
                values: {
                    address: saleComp.address,
                    saleDate: saleComp.saleDate,
                    salePrice: saleComp.salePrice,
                    grossPerAcre: saleComp.grossPerAcre,
                    zoning: saleComp.zoning,
                    buildingSize: saleComp.buildingSize,
                    parcelSizeAcres: saleComp.parcelSizeAcres,
                    landSizeUsable: saleComp.landSizeUsable,
                    buyerName: buyerPipedriveOrganization?.name || saleComp.buyerName,
                    isBuyerIndividual: !saleComp.pipedriveBuyerId && !!saleComp.buyerName,
                    sellerName: sellerPipedriveOrganization?.name || saleComp.sellerName,
                    isSellerIndividual: !saleComp.pipedriveSellerId && !!saleComp.sellerName,
                    notes: saleComp.notes,
                    propertyType: saleComp.propertyType,
                    dealProfile: saleComp.dealProfile,
                    noi: saleComp.noi,
                    capRate: saleComp.capRate,
                    isMarketed: saleComp.isMarketed,
                    brokerageName: brokeragePipedriveOrganization?.name || saleComp.brokerageName,
                    isBrokerageIndividual: !saleComp.pipedriveBrokerageId && !!saleComp.brokerageName,
                    buildingPerSqFt: saleComp.buildingPerSqFt,
                    pricePerUsableAcre: saleComp.pricePerUsableAcre,
                    compSource: saleComp.compSource,
                },
            });
        }

        return () => {
            saleCompStore.setEditedSaleCompId(null);
            mapMarkerStore.clear();
        };
    }, [saleComp, buyerPipedriveOrganization, sellerPipedriveOrganization, brokeragePipedriveOrganization]);

    if (saleCompStore.isSaleCompsLoading.fetching)
        return <OnMapSpinner />;

    if (!saleComp)
        return null;

    return (
        <DetailsPanel type={DetailsType.SaleCompEdit} onClose={close}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <SaleCompIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.primary}>{saleComp.address}</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <SaleCompFields formik={formik} />
            </div>
            <div className={commonStyles.separator}></div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}>
                    <EditControls
                        isStateChanged={formik.dirty || !mapMarkerStore.isMatch(saleComp.centerPoint)}
                        isUpdateLoading={saleCompStore.isSaleCompsLoading.updating! || pipedriveStore.isOrganizationsLoading.creating!}
                        onUpdate={formik.handleSubmit}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}>
                    <DeleteControl
                        entityName="Sale Comp"
                        isLoading={saleCompStore.isSaleCompsLoading.deleting!}
                        onDelete={remove}
                    />
                </div>
            </div>
        </DetailsPanel>
    );
}

export default observer(SaleCompEdit);